import Phaser from 'phaser';
import { Player } from './Player';

export type PowerUpType = 'speed' | 'invincibility' | 'damage' | 'magnet' | 'multi_shot' | 'time_slow';
export type PowerUpRarity = 'common' | 'rare' | 'epic';

export interface PowerUpInfo {
  type: PowerUpType;
  rarity: PowerUpRarity;
  duration: number;
  icon: string;
  name: string;
  color: number;
}

export class PowerUp extends Phaser.Physics.Arcade.Sprite {
  private player: Player;
  private powerUpType: PowerUpType;
  private powerUpRarity: PowerUpRarity;
  private magnetRange: number = 80;

  constructor(scene: Phaser.Scene, x: number, y: number, player: Player, type: PowerUpType, rarity: PowerUpRarity = 'common') {
    super(scene, x, y, '');

    this.player = player;
    this.powerUpType = type;
    this.powerUpRarity = rarity;

    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.createPowerUpTexture(scene);
    this.setTexture(`pickup-powerup-${type}-${rarity}`);

    // Add pulsing animation based on rarity
    const scale = rarity === 'epic' ? 1.3 : rarity === 'rare' ? 1.15 : 1;
    this.setScale(scale);

    scene.tweens.add({
      targets: this,
      scale: scale * 1.1,
      duration: rarity === 'epic' ? 500 : rarity === 'rare' ? 700 : 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private createPowerUpTexture(scene: Phaser.Scene): void {
    const graphics = scene.add.graphics();
    const size = 24;
    const halfSize = size / 2;

    let color, glowColor, symbol;

    switch (this.powerUpType) {
      case 'speed':
        color = 0x00FFFF;
        glowColor = 0x00DDDD;
        symbol = '⚡';
        break;
      case 'invincibility':
        color = 0xFFD700;
        glowColor = 0xFFB700;
        symbol = '★';
        break;
      case 'damage':
        color = 0xFF4500;
        glowColor = 0xDD3500;
        symbol = '⚔';
        break;
      case 'magnet':
        color = 0x9370DB;
        glowColor = 0x7350BB;
        symbol = '🧲';
        break;
      case 'multi_shot':
        color = 0x00FF00;
        glowColor = 0x00DD00;
        symbol = '⇉';
        break;
      case 'time_slow':
        color = 0x4169E1;
        glowColor = 0x2149C1;
        symbol = '⏰';
        break;
    }

    // Rarity modifier
    const rarityMultiplier = this.powerUpRarity === 'epic' ? 1.3 : this.powerUpRarity === 'rare' ? 1.15 : 1;
    const outerRadius = 11 * rarityMultiplier;
    const mainRadius = 9 * rarityMultiplier;
    const innerRadius = 7 * rarityMultiplier;

    // Outer glow (pulsing) - brighter for higher rarity
    const glowAlpha = this.powerUpRarity === 'epic' ? 0.7 : this.powerUpRarity === 'rare' ? 0.55 : 0.4;
    graphics.fillStyle(glowColor, glowAlpha);
    graphics.fillCircle(halfSize, halfSize, outerRadius);

    // Main circle
    graphics.fillStyle(color, 1);
    graphics.fillCircle(halfSize, halfSize, mainRadius);

    // Inner circle
    graphics.fillStyle(0xFFFFFF, 0.3);
    graphics.fillCircle(halfSize, halfSize, innerRadius);

    // Star points - more points for higher rarity
    const points = this.powerUpRarity === 'epic' ? 12 : this.powerUpRarity === 'rare' ? 10 : 8;
    for (let i = 0; i < points; i++) {
      const angle = (i / points) * Math.PI * 2;
      const x1 = halfSize + Math.cos(angle) * (4 * rarityMultiplier);
      const y1 = halfSize + Math.sin(angle) * (4 * rarityMultiplier);
      const x2 = halfSize + Math.cos(angle) * (9 * rarityMultiplier);
      const y2 = halfSize + Math.sin(angle) * (9 * rarityMultiplier);

      graphics.lineStyle(2 * rarityMultiplier, color, 0.6);
      graphics.lineBetween(x1, y1, x2, y2);
    }

    // Center highlight
    graphics.fillStyle(0xFFFFFF, 0.8);
    graphics.fillCircle(halfSize - 2, halfSize - 2, 2 * rarityMultiplier);

    graphics.generateTexture(`pickup-powerup-${this.powerUpType}-${this.powerUpRarity}`, size, size);
    graphics.destroy();
  }

  public setMagnetRange(range: number): void {
    this.magnetRange = range;
  }

  update(): void {
    if (!this.active || !this.player || !this.player.active) return;

    const distance = Phaser.Math.Distance.Between(
      this.x,
      this.y,
      this.player.x,
      this.player.y
    );

    // Magnetic pull
    if (distance < this.magnetRange) {
      const direction = new Phaser.Math.Vector2(
        this.player.x - this.x,
        this.player.y - this.y
      );

      direction.normalize();
      direction.scale(300);
      this.setVelocity(direction.x, direction.y);
    } else {
      this.setVelocity(0, 0);
    }

    // Collect if close enough
    if (distance < 20) {
      this.collect();
    }
  }

  private collect(): void {
    let message = '';
    let duration = 10000; // Base 10 seconds
    let icon = '';
    let color = '#FFD700';

    // Rarity affects duration
    const rarityMultiplier = this.powerUpRarity === 'epic' ? 1.5 : this.powerUpRarity === 'rare' ? 1.25 : 1;
    duration = Math.floor(duration * rarityMultiplier);

    switch (this.powerUpType) {
      case 'speed':
        message = 'SPEED BOOST!';
        icon = '⚡';
        color = '#00FFFF';
        this.applySpeedBoost(duration);
        break;
      case 'invincibility':
        message = 'INVINCIBLE!';
        duration = Math.floor(5000 * rarityMultiplier); // Base 5 seconds
        icon = '★';
        color = '#FFD700';
        this.applyInvincibility(duration);
        break;
      case 'damage':
        message = 'DAMAGE UP!';
        icon = '⚔️';
        color = '#FF4500';
        this.applyDamageBoost(duration);
        break;
      case 'magnet':
        message = 'SUPER MAGNET!';
        icon = '🧲';
        color = '#9370DB';
        this.applyMagnetBoost(duration);
        break;
      case 'multi_shot':
        message = 'MULTI SHOT!';
        icon = '⇉';
        color = '#00FF00';
        this.applyMultiShot(duration);
        break;
      case 'time_slow':
        message = 'TIME SLOW!';
        duration = Math.floor(8000 * rarityMultiplier); // Base 8 seconds
        icon = '⏰';
        color = '#4169E1';
        this.applyTimeSlow(duration);
        break;
    }

    // Emit event for UI to display active power-up
    const powerUpInfo: PowerUpInfo = {
      type: this.powerUpType,
      rarity: this.powerUpRarity,
      duration: duration,
      icon: icon,
      name: message,
      color: parseInt(color.replace('#', '0x'))
    };
    this.scene.events.emit('powerup-collected', powerUpInfo);

    // Show message with rarity indicator
    const rarityText = this.powerUpRarity === 'epic' ? '⭐⭐⭐' : this.powerUpRarity === 'rare' ? '⭐⭐' : '⭐';
    const powerUpText = this.scene.add.text(this.x, this.y, `${rarityText}\n${message}`, {
      fontSize: this.powerUpRarity === 'epic' ? '24px' : '20px',
      color: color,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
      align: 'center'
    });
    powerUpText.setOrigin(0.5);
    powerUpText.setDepth(1000);

    this.scene.tweens.add({
      targets: powerUpText,
      y: powerUpText.y - 60,
      alpha: 0,
      scale: 1.2,
      duration: 2000,
      onComplete: () => {
        powerUpText.destroy();
      }
    });

    this.destroy();
  }

  private applySpeedBoost(duration: number): void {
    const originalSpeed = this.player.getData('moveSpeed') || 200;
    this.player.setData('moveSpeed', originalSpeed * 1.5);
    this.player.setTint(0x00FFFF);

    this.scene.time.delayedCall(duration, () => {
      this.player.setData('moveSpeed', originalSpeed);
      this.player.clearTint();
    });
  }

  private applyInvincibility(duration: number): void {
    this.player.setData('invincible', true);
    this.player.setTint(0xFFD700);

    // Flashing effect
    const flashTimer = this.scene.time.addEvent({
      delay: 100,
      callback: () => {
        const currentAlpha = this.player.alpha;
        this.player.setAlpha(currentAlpha === 1 ? 0.5 : 1);
      },
      loop: true
    });

    this.scene.time.delayedCall(duration, () => {
      this.player.setData('invincible', false);
      this.player.clearTint();
      this.player.setAlpha(1);
      flashTimer.destroy();
    });
  }

  private applyDamageBoost(duration: number): void {
    // This would need to integrate with the weapon system
    // For now, we'll just show the effect
    this.player.setTint(0xFF4500);

    this.scene.time.delayedCall(duration, () => {
      this.player.clearTint();
    });

    // Emit event for weapon system to listen to
    this.scene.events.emit('damage-boost-start', 2); // 2x damage multiplier
    this.scene.time.delayedCall(duration, () => {
      this.scene.events.emit('damage-boost-end');
    });
  }

  private applyMagnetBoost(duration: number): void {
    this.player.setTint(0x9370DB);

    // Increase magnet range on all pickups
    this.scene.events.emit('magnet-boost-start', 300); // Large magnet range

    this.scene.time.delayedCall(duration, () => {
      this.player.clearTint();
      this.scene.events.emit('magnet-boost-end');
    });
  }

  private applyMultiShot(duration: number): void {
    this.player.setTint(0x00FF00);

    // Emit event for weapon system to add extra projectiles
    this.scene.events.emit('multi-shot-start', 2); // Fire 2 extra projectiles

    this.scene.time.delayedCall(duration, () => {
      this.player.clearTint();
      this.scene.events.emit('multi-shot-end');
    });
  }

  private applyTimeSlow(duration: number): void {
    this.player.setTint(0x4169E1);

    // Slow down enemy movement and game time
    this.scene.events.emit('time-slow-start', 0.5); // 50% speed

    this.scene.time.delayedCall(duration, () => {
      this.player.clearTint();
      this.scene.events.emit('time-slow-end');
    });
  }
}
